import { useCallback, useMemo } from 'react';

import { isCommunityAdmin, isCommunityMember } from 'common/utils';
import { selectMyCommunityId, useGetCurrentCommunityMemberQuery } from 'community/api';
import { UserPrivilegeDetails, UserPrivilegeName } from 'domain/UserProfile';
import { useAppSelector } from 'store';
import { selectUserAccount } from 'user/api';

export const useAuthorization = () => {
  const myCommunityId = useAppSelector(selectMyCommunityId);
  const account = useAppSelector(selectUserAccount);
  /**
   * `currentData`, not `data`: the two differ for exactly as long as a community switch is in
   * flight, and that gap is a live over-grant.
   *
   * Switching community changes this query's argument, and across an argument change RTK Query
   * keeps serving the previous argument's result. `isLoading` does not cover the window either -
   * it means "no data at all for this hook", and the previous community's record is still there,
   * so it stays false throughout. Every gate below therefore answered the new community from the
   * old community's privileges and role.
   *
   * `currentData` is the cache entry for the argument actually being asked about, so it is
   * undefined precisely when this community has no answer yet. A failed refetch of a community
   * already answered still answers: `queryThunk.rejected` sets `status` and `error` and never
   * touches the data slot.
   */
  const {
    currentData: member,
    isLoading,
    isFetching,
    isError,
  } = useGetCurrentCommunityMemberQuery({ communityId: myCommunityId }, { skip: !myCommunityId });
  const { communityRole, privileges: userPrivileges } = member ?? {};

  const isActivityAllowed = useMemo<Record<UserPrivilegeName, boolean>>(
    () =>
      Object.values(UserPrivilegeDetails).reduce(
        (allPrivileges, { id, name }) => {
          allPrivileges[name] = !!userPrivileges?.some((up) => up.id === id);
          return allPrivileges;
        },
        {} as Record<UserPrivilegeName, boolean>
      ),
    [userPrivileges]
  );

  const hasPrivilege = useCallback(
    (privilegeNames: UserPrivilegeName[]) => privilegeNames?.some((p) => !!userPrivileges?.some((ap) => ap.name === p)),
    [userPrivileges]
  );

  /**
   * Pending rather than denying while the switch lands - the answer is not yet known, which is not
   * the same as known to be no, and three of the guards below redirect on a no.
   *
   * Deliberately separate from `isLoading` rather than folded into it. `withUserRole` blocks the
   * whole Dashboard on `isLoading`, and `Loadable` swaps its children out rather than overlaying
   * them, so widening that flag would unmount and remount everything below on every switch and
   * throw away its state. Only the guards that would otherwise bounce the user read this one.
   */
  const isAnswerPending = member === undefined && isFetching;

  return {
    communityMember: member,
    isError,
    isLoading,
    isAnswerPending,
    isCommunityAdmin: isCommunityAdmin({ communityRole }),
    isCommunityMember: isCommunityMember({ communityRole }),
    isWithoutCommunity: !myCommunityId,
    isOrganizationMember: !!account?.isOrganizationMember,
    isCompanyAdmin: !!account?.isCompanyAdmin,
    hasPrivilege,
    isActivityAllowed,
  };
};
